/**
 * PrepBatchView — log a prep batch.
 *
 * Select a prep recipe, enter the actual quantity produced.
 * On confirm:
 *   1. Deducts each raw ingredient proportionally (actual / recipe_yield).
 *   2. Credits the linked prep ingredient with the actual quantity produced.
 *   3. Writes inventory_transaction rows for everything.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd, formatNumber, resolveAppLocale } from '../locale.js';
import {
  computeBatchRawCost,
  computeBatchUnitCost,
  computePrepStandardCost,
  nextPrepIngredientCost,
} from '@cafe-pos/shared';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { AlertTriangle } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type RecipeRow = Database['public']['Tables']['recipes']['Row'];
type RecipeItemRow = Database['public']['Tables']['recipe_items']['Row'];
type IngredientRow = Database['public']['Tables']['ingredients']['Row'];

const roundCost = (n: number) => Math.round(n * 100) / 100;

function toCostMap(ingredients: Map<string, IngredientRow>): Map<string, { id: string; cost_price: number; name: string }> {
  return new Map(
    [...ingredients.values()].map((i) => [
      i.id,
      { id: i.id, cost_price: Number(i.cost_price ?? 0), name: i.name },
    ]),
  );
}

interface PrepRecipe extends RecipeRow {
  is_prep: true;
  prep_yield: number;
  prep_unit: string;
  prep_ingredient_id: string;
  items: (RecipeItemRow & {
    ingredient_name: string;
    ingredient_unit: string;
    sub_recipe_name: string | null;
  })[];
}

interface BatchLog {
  id: string;
  recipe_name: string;
  qty_produced: number;
  unit: string;
  created_at: string;
  note: string | null;
  logged_by: string | null;
}

export function PrepBatchView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const fmtNum = (n: number) => formatNumber(n, i18n.language, { maximumFractionDigits: 3 });
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
  const fmtCost = (n: number) => formatAmd(n, i18n.language);
  const [prepRecipes, setPrepRecipes] = useState<PrepRecipe[]>([]);
  const [ingredients, setIngredients] = useState<Map<string, IngredientRow>>(new Map());
  const [logs, setLogs] = useState<BatchLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Form
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [qtyInput, setQtyInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [recalcSaving, setRecalcSaving] = useState(false);
  const [recalcResult, setRecalcResult] = useState<string | null>(null);

  // Pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);

  async function load() {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError(null);

    const [recipeRes, itemRes, ingRes] = await Promise.all([
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any)
        .from('recipes')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_prep', true)
        .order('name') as Promise<{ data: RecipeRow[] | null; error: unknown }>,
      supabase.from('recipe_items').select('*'),
      supabase.from('ingredients').select('*').eq('client_id', clientId),
    ]);

    if ((recipeRes as { error: unknown }).error) {
      setError(String((recipeRes as { error: { message: string } }).error.message));
      setLoading(false);
      return;
    }
    if (ingRes.error) {
      setError(ingRes.error.message);
      setLoading(false);
      return;
    }

    const ingMap = new Map((ingRes.data ?? []).map((i) => [i.id, i]));
    setIngredients(ingMap);

    const allItems = itemRes.data ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recipeData = ((recipeRes as any).data ?? []) as RecipeRow[];
    const recipes: PrepRecipe[] = recipeData
      .filter(
        (r) =>
          (r as any).is_prep &&
          (r as any).prep_yield &&
          (r as any).prep_unit &&
          (r as any).prep_ingredient_id,
      )
      .map((r) => ({
        ...r,
        is_prep: true as const,
        prep_yield: (r as any).prep_yield as number,
        prep_unit: (r as any).prep_unit as string,
        prep_ingredient_id: (r as any).prep_ingredient_id as string,
        items: allItems
          .filter((it) => it.recipe_id === r.id)
          .map((it) => ({
            ...it,
            ingredient_name: it.ingredient_id ? (ingMap.get(it.ingredient_id)?.name ?? '?') : '',
            ingredient_unit: it.ingredient_id ? (ingMap.get(it.ingredient_id)?.unit ?? '') : '',
            sub_recipe_name: it.sub_recipe_id
              ? (recipeData.find((rr) => rr.id === it.sub_recipe_id)?.name ?? '?')
              : null,
          })),
      }));

    setPrepRecipes(recipes);

    // Load recent prep transactions as batch log
    const prepIngIds = recipes.map((r) => r.prep_ingredient_id);
    if (prepIngIds.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: txs } = (await (supabase as any)
        .from('inventory_transactions')
        .select('id, ingredient_id, delta, note, created_at, created_by')
        .in('ingredient_id', prepIngIds)
        .eq('type', 'prep')
        .order('created_at', { ascending: false })
        .limit(200)) as {
        data:
          | {
              id: string;
              ingredient_id: string;
              delta: number;
              note: string | null;
              created_at: string;
              created_by: string | null;
            }[]
          | null;
      };

      // Resolve profile names for all unique created_by UUIDs
      const creatorIds = [
        ...new Set((txs ?? []).map((t) => t.created_by).filter(Boolean)),
      ] as string[];
      const profileMap = new Map<string, string>();
      if (creatorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, full_name')
          .in('id', creatorIds);
        for (const p of profiles ?? []) {
          profileMap.set(p.id, p.full_name ?? p.username ?? p.id);
        }
      }

      const batchLogs: BatchLog[] = (txs ?? []).map((tx) => {
        const recipe = recipes.find((r) => r.prep_ingredient_id === tx.ingredient_id);
        return {
          id: tx.id,
          recipe_name: recipe?.name ?? '?',
          qty_produced: tx.delta,
          unit: recipe?.prep_unit ?? '',
          created_at: tx.created_at,
          note: tx.note,
          logged_by: tx.created_by ? (profileMap.get(tx.created_by) ?? null) : null,
        };
      });
      setLogs(batchLogs);
    } else {
      setLogs([]);
    }

    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRecipe = prepRecipes.find((r) => r.id === selectedRecipeId) ?? null;
  const actualQty = Number(qtyInput) || 0;

  /** Ingredients this batch will consume, scaled to actual qty produced. */
  function previewDeductions(): { name: string; unit: string; qty: number; available: number }[] {
    if (!selectedRecipe || actualQty <= 0) return [];
    // Items define quantities for the full yield batch (prep_yield).
    // Scale proportionally when producing a different qty.
    const scale = actualQty / selectedRecipe.prep_yield;
    return selectedRecipe.items
      .filter((it) => it.ingredient_id)
      .map((it) => {
        const ing = ingredients.get(it.ingredient_id!);
        return {
          name: it.ingredient_name,
          unit: it.ingredient_unit,
          qty: Number(it.quantity) * scale,
          available: Number(ing?.stock ?? 0),
        };
      });
  }

  const deductions = previewDeductions();
  const insufficientStock = deductions.some((d) => d.qty > d.available + 0.0001);

  const batchCostPreview =
    selectedRecipe && actualQty > 0
      ? (() => {
          const scale = actualQty / selectedRecipe.prep_yield;
          const { rawCost, missingCosts } = computeBatchRawCost(
            selectedRecipe.items,
            scale,
            toCostMap(ingredients),
          );
          return {
            rawCost,
            unitCost: computeBatchUnitCost(rawCost, actualQty),
            missingCosts,
          };
        })()
      : null;

  async function recalculateAllPrepCosts() {
    if (!supabase || prepRecipes.length === 0) return;
    if (
      !confirm(
        t('prepBatches.recalcConfirm'),
      )
    ) {
      return;
    }
    setRecalcSaving(true);
    setRecalcResult(null);
    setError(null);
    const costMap = toCostMap(ingredients);
    const updated: string[] = [];
    const skipped: string[] = [];

    try {
      for (const recipe of prepRecipes) {
        const result = computePrepStandardCost(
          recipe.prep_yield,
          recipe.items,
          costMap,
        );
        if (result.missingCosts.length > 0) {
          skipped.push(
            `${recipe.name} (missing cost: ${result.missingCosts.map((m) => m.name).join(', ')})`,
          );
          continue;
        }
        const { error: err } = await supabase
          .from('ingredients')
          .update({ cost_price: roundCost(result.unitCost) })
          .eq('id', recipe.prep_ingredient_id);
        if (err) {
          skipped.push(`${recipe.name} (${err.message})`);
        } else {
          updated.push(`${recipe.name} → ${fmtCost(result.unitCost)}/${recipe.prep_unit}`);
        }
      }
      const parts: string[] = [];
      if (updated.length) parts.push(t('prepBatches.recalcUpdated', { count: updated.length, list: updated.join('; ') }));
      if (skipped.length) parts.push(t('prepBatches.recalcSkipped', { count: skipped.length, list: skipped.join('; ') }));
      setRecalcResult(parts.join('. ') || t('prepBatches.recalcNothing'));
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('prepBatches.recalcFailed'));
    } finally {
      setRecalcSaving(false);
    }
  }

  async function logBatch() {
    if (!supabase || !clientId || !selectedRecipe || actualQty <= 0) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    const {
      data: { session },
    } = await supabase.auth.getSession();
    const userId = session?.user.id ?? null;

    // Items define quantities for the full yield batch (prep_yield).
    const scale = actualQty / selectedRecipe.prep_yield;
    const now = new Date().toISOString();
    const costMap = toCostMap(ingredients);
    const { rawCost } = computeBatchRawCost(selectedRecipe.items, scale, costMap);
    const batchUnitCost = computeBatchUnitCost(rawCost, actualQty);

    try {
      // 1. Deduct raw ingredients
      for (const it of selectedRecipe.items.filter((x) => x.ingredient_id)) {
        const deductQty = Number(it.quantity) * scale;
        const ing = ingredients.get(it.ingredient_id!);
        if (!ing) continue;
        const newStock = Math.max(0, Number(ing.stock) - deductQty);
        await supabase.from('ingredients').update({ stock: newStock }).eq('id', ing.id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any).from('inventory_transactions').insert({
          client_id: clientId,
          ingredient_id: ing.id,
          delta: -deductQty,
          type: 'prep',
          created_by: userId,
          note: `Prep batch: ${selectedRecipe.name} × ${fmtNum(actualQty)} ${selectedRecipe.prep_unit}${noteInput.trim() ? ` — ${noteInput.trim()}` : ''}`,
          created_at: now,
        });
      }

      // 2. Credit the prep ingredient (stock + weighted-average cost)
      const prepIng = ingredients.get(selectedRecipe.prep_ingredient_id);
      const currentStock = Number(prepIng?.stock ?? 0);
      const currentCost = Number(prepIng?.cost_price ?? 0);
      const newCost = roundCost(
        nextPrepIngredientCost(currentStock, currentCost, actualQty, batchUnitCost),
      );
      await supabase
        .from('ingredients')
        .update({ stock: currentStock + actualQty, cost_price: newCost })
        .eq('id', selectedRecipe.prep_ingredient_id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any).from('inventory_transactions').insert({
        client_id: clientId,
        ingredient_id: selectedRecipe.prep_ingredient_id,
        delta: actualQty,
        type: 'prep',
        created_by: userId,
        note: `Prep batch produced${noteInput.trim() ? ` — ${noteInput.trim()}` : ''}`,
        created_at: now,
      });

      setSuccess(
        t('prepBatches.success', { qty: fmtNum(actualQty), unit: selectedRecipe.prep_unit, name: selectedRecipe.name }) +
          (batchUnitCost > 0 ? ` · unit cost ${fmtCost(batchUnitCost)}` : ''),
      );
      setSelectedRecipeId('');
      setQtyInput('');
      setNoteInput('');
      setConfirming(false);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('prepBatches.logFailed'));
    } finally {
      setSaving(false);
    }
  }

  const pagedLogs = logs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('prepBatches.title')}
        subtitle={t('prepBatches.subtitle')}
        actions={
          prepRecipes.length > 0 ? (
            <button
              className="btn-ghost btn-sm"
              onClick={() => void recalculateAllPrepCosts()}
              disabled={recalcSaving || loading}
              title={t('prepBatches.recalcTitle')}
            >
              {recalcSaving ? '…' : t('prepBatches.recalcAll')}
            </button>
          ) : undefined
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}
      {recalcResult && (
        <div className="success-banner" style={{ marginBottom: 12 }}>
          {recalcResult}
        </div>
      )}
      {success && (
        <div className="success-banner" style={{ marginBottom: 12 }}>
          {success}
        </div>
      )}

      {loading ? (
        <div className="empty-state">{t('common.loading')}</div>
      ) : prepRecipes.length === 0 ? (
        <div className="empty-state">
          {t('prepBatches.emptyRecipes')}
        </div>
      ) : (
        <>
          {/* ── Log batch form ── */}
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header">{t('prepBatches.logPanel')}</div>
            <div
              style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={selectedRecipeId}
                  onChange={(e) => {
                    const recipe = prepRecipes.find((r) => r.id === e.target.value);
                    setSelectedRecipeId(e.target.value);
                    setQtyInput(recipe ? String(recipe.prep_yield) : '');
                    setConfirming(false);
                  }}
                  style={{ minWidth: 200 }}
                >
                  <option value="">{t('prepBatches.selectRecipe')}</option>
                  {prepRecipes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {t('prepBatches.recipeOption', { name: r.name, yield: fmtNum(r.prep_yield), unit: r.prep_unit })}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  placeholder={
                    selectedRecipe ? t('prepBatches.qtyWithUnit', { unit: selectedRecipe.prep_unit }) : t('prepBatches.qtyPlaceholder')
                  }
                  value={qtyInput}
                  onChange={(e) => {
                    setQtyInput(e.target.value);
                    setConfirming(false);
                  }}
                  style={{ width: 160 }}
                  disabled={!selectedRecipe}
                />
                <input
                  placeholder={t('prepBatches.notePlaceholder')}
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  style={{ flex: 1, minWidth: 160 }}
                  disabled={!selectedRecipe}
                />
              </div>

              {/* Preview deductions */}
              {selectedRecipe && actualQty > 0 && (
                <div
                  style={{
                    background: 'var(--surface-2)',
                    borderRadius: 6,
                    padding: '10px 14px',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    {t('prepBatches.previewTitle', { qty: fmtNum(actualQty), unit: selectedRecipe.prep_unit })}
                    <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 6, opacity: 0.6 }}>
                      {t('prepBatches.previewYield', {
                        yield: fmtNum(selectedRecipe.prep_yield),
                        unit: selectedRecipe.prep_unit,
                      })}
                    </span>
                  </div>
                  <table className="table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>{t('prepBatches.colIngredient')}</th>
                        <th style={{ textAlign: 'right' }}>{t('prepBatches.colDeduct')}</th>
                        <th style={{ textAlign: 'right' }}>{t('prepBatches.colInStock')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {deductions.map((d) => (
                        <tr key={d.name}>
                          <td>{d.name}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtNum(d.qty)} {d.unit}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                              color: d.available < d.qty ? 'var(--danger)' : 'var(--text-muted)',
                            }}
                          >
                            {fmtNum(d.available)} {d.unit}
                          </td>
                          <td>
                            {d.qty > d.available + 0.0001 && (
                              <span style={{ color: 'var(--danger)', fontSize: 12 }}><AlertTriangle className="ui-i" /> {t('prepBatches.low')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {selectedRecipe.items.some((it) => it.sub_recipe_id) && (
                        <tr>
                          <td colSpan={4} style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            <AlertTriangle className="ui-i" /> {t('prepBatches.subRecipeWarning')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {insufficientStock && (
                    <div className="warning-banner" style={{ marginTop: 8, marginBottom: 0 }}>
                      <AlertTriangle className="ui-i" /> {t('prepBatches.insufficient')}
                    </div>
                  )}
                  {batchCostPreview && batchCostPreview.rawCost > 0 && (
                    <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                      {t('prepBatches.batchCost', {
                        raw: fmtCost(batchCostPreview.rawCost),
                        unitCost: fmtCost(batchCostPreview.unitCost),
                        unit: selectedRecipe.prep_unit,
                      })}
                    </div>
                  )}
                  {batchCostPreview && batchCostPreview.missingCosts.length > 0 && (
                    <div className="warning-banner" style={{ marginTop: 8, marginBottom: 0 }}>
                      <AlertTriangle className="ui-i" /> {t('prepBatches.missingCost', {
                        names: batchCostPreview.missingCosts.map((m) => m.name).join(', '),
                      })}
                    </div>
                  )}
                </div>
              )}

              {!confirming ? (
                <button
                  className="btn"
                  style={{ alignSelf: 'flex-start' }}
                  disabled={!selectedRecipe || actualQty <= 0}
                  onClick={() => setConfirming(true)}
                >
                  {t('prepBatches.reviewConfirm')}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 13 }}>
                    {t('prepBatches.confirmPrompt', {
                      qty: fmtNum(actualQty),
                      unit: selectedRecipe?.prep_unit,
                      name: selectedRecipe?.name,
                    })}
                  </span>
                  <button className="btn" onClick={() => void logBatch()} disabled={saving}>
                    {saving ? '…' : t('prepBatches.confirm')}
                  </button>
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => setConfirming(false)}
                    disabled={saving}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Batch log ── */}
          <div className="panel">
            <div className="panel-header">{t('prepBatches.historyTitle')}</div>
            {logs.length === 0 ? (
              <div className="empty-state">{t('prepBatches.empty')}</div>
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('prepBatches.colDate')}</th>
                      <th>{t('prepBatches.colPrep')}</th>
                      <th style={{ textAlign: 'right' }}>{t('prepBatches.colQty')}</th>
                      <th>{t('prepBatches.colBy')}</th>
                      <th>{t('prepBatches.colNote')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLogs.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <span className="muted" style={{ fontSize: 12 }}>
                            {fmt(l.created_at)}
                          </span>
                        </td>
                        <td style={{ fontWeight: 500 }}>{l.recipe_name}</td>
                        <td
                          style={{
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--success)',
                          }}
                        >
                          +{fmtNum(l.qty_produced)} {l.unit}
                        </td>
                        <td>
                          <span className="muted" style={{ fontSize: 12 }}>
                            {l.logged_by ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span className="muted" style={{ fontSize: 12 }}>
                            {l.note ?? '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {logs.length > PAGE_SIZE && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 16px 12px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <button
                      className="btn-ghost btn-sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {page + 1} / {Math.ceil(logs.length / PAGE_SIZE)}
                    </span>
                    <button
                      className="btn-ghost btn-sm"
                      disabled={(page + 1) * PAGE_SIZE >= logs.length}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}
